import { buildUniqueSelector } from './selector-utils';

/**
 * Low Color Contrast Rule
 *
 * Detects text elements with insufficient contrast against their background,
 * following WCAG 2.1 Level AA requirements:
 *   - Normal text: minimum 4.5:1 contrast ratio
 *   - Large text (>=24px, or >=18.66px bold): minimum 3:1 contrast ratio
 *
 * Background detection strategy:
 *   - Walk the DOM tree to find computed background-color values,
 *     compositing semi-transparent layers bottom-up over default white.
 *   - Detect CSS background-image / intersecting <img> for flagging.
 */
export class LowColorContrastRule {
	constructor(i18n) {
		this.__ = i18n;
		this.key = 'lowColorContrast';
		this.maxChecks = 300;
	}

	getDefaultStatus() {
		return 'not_checked';
	}

	evaluate(documentNode) {
		const defaultView = documentNode.defaultView || window;

		const textElements = this.getVisibleTextElements(documentNode, defaultView);
		const checks = [];
		const issues = [];

		for (const element of textElements.slice(0, this.maxChecks)) {
			const check = this.buildContrastCheck(element, defaultView);

			if (!check) {
				continue;
			}

			checks.push(check);

			if (check.isFail) {
				issues.push(check);
			}
		}

		if (checks.length === 0) {
			return {
				status: 'pass',
				message: this.__( 'No visible text nodes found.', 'a11yfix' ),
				details: { checks, issues }
			};
		}

		if (issues.length > 0) {
			return {
				status: 'fail',
				message: `${this.__( 'Low contrast text nodes found:', 'a11yfix' )} ${issues.length}`,
				details: { checks, issues }
			};
		}

		return {
			status: 'pass',
			message: this.__( 'No low contrast text nodes found.', 'a11yfix' ),
			details: { checks, issues }
		};
	}

	/**
	 * Collect unique parent elements of visible text nodes,
	 * excluding admin bar, scripts, styles, and hidden elements.
	 */
	getVisibleTextElements(documentNode, defaultView) {
		const elements = new Set();
		const root = documentNode.body || documentNode;
		const walker = documentNode.createTreeWalker(root, NodeFilter.SHOW_TEXT);
		let node = walker.nextNode();

		while (node) {
			const text = (node.textContent || '').replace(/\s+/g, ' ').trim();

			if (text && node.parentElement) {
				elements.add(node.parentElement);
			}

			node = walker.nextNode();
		}

		return Array.from(elements).filter((el) => {
			if (el.closest('#wpadminbar, script, style, noscript')) {
				return false;
			}

			const text = (el.textContent || '').replace(/\s+/g, ' ').trim();

			if (!text) {
				return false;
			}

			const style = defaultView.getComputedStyle(el);

			if (style.display === 'none' || style.visibility === 'hidden') {
				return false;
			}

			if (Number.parseFloat(style.opacity || '1') === 0) {
				return false;
			}

			return true;
		});
	}

	/**
	 * Build a contrast check result for a single text element.
	 */
	buildContrastCheck(node, defaultView) {
		const style = defaultView.getComputedStyle(node);
		const text = (node.textContent || '').replace(/\s+/g, ' ').trim();
		const foreground = this.parseColor(style.color);

		if (!foreground) {
			return null;
		}

		const bgResult = this.resolveBackground(node, defaultView);

		if (bgResult.candidates.length === 0) {
			return null;
		}

		// Find the background candidate that produces the worst (lowest) contrast.
		let worstRatio = Number.POSITIVE_INFINITY;
		let worstBackground = null;

		for (const candidate of bgResult.candidates) {
			const ratio = this.calculateContrastRatio(foreground, candidate);

			if (ratio < worstRatio) {
				worstRatio = ratio;
				worstBackground = candidate;
			}
		}

		if (!worstBackground) {
			return null;
		}

		const fontSizePx = Number.parseFloat(style.fontSize || '16') || 16;
		const fontWeight = Number.parseInt(style.fontWeight || '400', 10) || 400;
		const isLargeText = fontSizePx >= 24 || (fontSizePx >= 18.66 && fontWeight >= 700);
		const threshold = isLargeText ? 3 : 4.5;

		return {
			tag: node.tagName.toLowerCase(),
			selector: buildUniqueSelector(node),
			text: text.slice(0, 140),
			contrastRatio: Number(worstRatio.toFixed(2)),
			threshold,
			isLargeText,
			isFail: worstRatio < threshold,
			backgroundSource: bgResult.source,
			hasComplexBackground: bgResult.hasComplexBackground,
			hasImageIntersection: bgResult.hasImageIntersection,
			foreground: this.toRgbString(foreground),
			background: this.toRgbString(worstBackground)
		};
	}

	/**
	 * Resolve the effective background behind a text element.
	 */
	resolveBackground(node, defaultView) {
		const complexBackground = this.detectComplexBackground(node, defaultView);
		const hasComplexBg = complexBackground.hasComplexBackground;
		const hasImageIntersectionBg = complexBackground.hasImageIntersection;

		// Walk DOM ancestors for computed background-color.
		const solidBg = this.resolveSolidBackgroundColor(node, defaultView);

		if (!solidBg) {
			return {
				candidates: [],
				source: 'none',
				hasComplexBackground: hasComplexBg,
				hasImageIntersection: hasImageIntersectionBg
			};
		}

		return {
			candidates: [solidBg],
			source: hasComplexBg ? 'solid-with-complex' : 'solid',
			hasComplexBackground: hasComplexBg,
			hasImageIntersection: hasImageIntersectionBg
		};
	}

	/**
	 * Detect whether the text element sits over a complex (non-solid) background:
	 *   - CSS background-image on any ancestor element
	 *   - An <img> element whose bounding rect overlaps the text
	 */
	detectComplexBackground(node, defaultView) {
		// Walk ancestors for CSS background-image.
		let current = node;

		while (current && current.nodeType === Node.ELEMENT_NODE) {
			try {
				const style = defaultView.getComputedStyle(current);
				const bgImage = style.backgroundImage || '';

				if (bgImage && bgImage !== 'none') {
					return {
						hasComplexBackground: true,
						hasImageIntersection: false
					};
				}
			} catch {
				// Skip elements that can't be styled.
			}

			current = current.parentElement;
		}

		// Check for intersecting <img> elements.
		const textRect = node.getBoundingClientRect();

		if (textRect.width <= 0 || textRect.height <= 0) {
			return {
				hasComplexBackground: false,
				hasImageIntersection: false
			};
		}

		const images = node.ownerDocument.querySelectorAll('img');

		for (const img of images) {
			if (!img.complete || img.naturalWidth === 0) {
				continue;
			}

			if (img.closest('#wpadminbar')) {
				continue;
			}

			const imgStyle = defaultView.getComputedStyle(img);

			if (imgStyle.display === 'none' || imgStyle.visibility === 'hidden') {
				continue;
			}

			if (Number.parseFloat(imgStyle.opacity || '1') === 0) {
				continue;
			}

			const imgRect = img.getBoundingClientRect();

			if (imgRect.width <= 0 || imgRect.height <= 0) {
				continue;
			}

			const intersects = !(
				textRect.right <= imgRect.left ||
				textRect.left >= imgRect.right ||
				textRect.bottom <= imgRect.top ||
				textRect.top >= imgRect.bottom
			);

			if (intersects) {
				return {
					hasComplexBackground: true,
					hasImageIntersection: true
				};
			}
		}

		return {
			hasComplexBackground: false,
			hasImageIntersection: false
		};
	}

	/**
	 * Walk DOM ancestors to find the effective background-color.
	 * Composites semi-transparent layers bottom-up over default white.
	 */
	resolveSolidBackgroundColor(node, defaultView) {
		const layers = [];
		let current = node;

		while (current && current.nodeType === Node.ELEMENT_NODE) {
			try {
				const style = defaultView.getComputedStyle(current);
				const parsed = this.parseColor(style.backgroundColor || '');

				if (parsed && parsed.alpha > 0) {
					layers.push(parsed);

					// Fully opaque layer blocks everything behind it.
					if (parsed.alpha >= 1) {
						break;
					}
				}
			} catch {
				// Skip unstyled elements.
			}

			current = current.parentElement;
		}

		// Composite from back (deepest ancestor) to front (node itself)
		// over the default white page background.
		let result = { red: 255, green: 255, blue: 255, alpha: 1 };

		for (let i = layers.length - 1; i >= 0; i -= 1) {
			result = this.compositeOver(layers[i], result);
		}

		result.layerCount = layers.length;

		return result;
	}

	/**
	 * Alpha-composite a foreground color over an opaque background color.
	 */
	compositeOver(fg, bg) {
		const a = fg.alpha;

		return {
			red: Math.round(fg.red * a + bg.red * (1 - a)),
			green: Math.round(fg.green * a + bg.green * (1 - a)),
			blue: Math.round(fg.blue * a + bg.blue * (1 - a)),
			alpha: 1
		};
	}

	// --- Color utility methods ---

	parseColor(rawColor) {
		if (!rawColor) {
			return null;
		}

		const rgbMatch = rawColor.match(/rgba?\(([^)]+)\)/i);

		if (!rgbMatch) {
			return null;
		}

		const parts = rgbMatch[1].split(',').map((part) => part.trim());

		if (parts.length < 3) {
			return null;
		}

		const red = Number.parseFloat(parts[0]);
		const green = Number.parseFloat(parts[1]);
		const blue = Number.parseFloat(parts[2]);
		const alpha = parts[3] === undefined ? 1 : Number.parseFloat(parts[3]);

		if ([red, green, blue].some((value) => Number.isNaN(value))) {
			return null;
		}

		return {
			red,
			green,
			blue,
			alpha: Number.isNaN(alpha) ? 1 : alpha
		};
	}

	calculateContrastRatio(foreground, background) {
		const foregroundLuminance = this.calculateRelativeLuminance(foreground);
		const backgroundLuminance = this.calculateRelativeLuminance(background);
		const lighter = Math.max(foregroundLuminance, backgroundLuminance);
		const darker = Math.min(foregroundLuminance, backgroundLuminance);

		return (lighter + 0.05) / (darker + 0.05);
	}

	calculateRelativeLuminance(color) {
		const red = this.linearize(color.red);
		const green = this.linearize(color.green);
		const blue = this.linearize(color.blue);

		return red * 0.2126 + green * 0.7152 + blue * 0.0722;
	}

	linearize(channel) {
		const normalized = channel / 255;

		if (normalized <= 0.03928) {
			return normalized / 12.92;
		}

		return ((normalized + 0.055) / 1.055) ** 2.4;
	}

	toRgbString(color) {
		return `rgb(${Math.round(color.red)}, ${Math.round(color.green)}, ${Math.round(color.blue)})`;
	}
}
